/**
 * Single-seller retail mode.
 *
 * cardsimp.com is currently dedicated to ONE seller (the CardSimp store)
 * operating it as a retail storefront. All marketplace plumbing (other
 * sellers, buyer accounts, messaging, offers, trades, market discovery, etc.)
 * is preserved in the codebase + database for a future flip back, but is hidden
 * from any unauthenticated visitor.
 *
 * Owner (admin) sign-in path: `/owner`. Once signed in as admin, the FULL app
 * mounts unchanged.
 *
 * To re-enable marketplace mode in the future:
 *   1. Flip RETAIL_MODE to false here.
 *   2. Clear `archived_at` on the non-CardSimp profiles.
 *   3. Re-create `public_user_items` without the per-user_id lock
 *      (drop the `WHERE user_id = OWNER_USER_ID` clause).
 *   4. Re-add Header/marketing chrome on signed-out routes if removed.
 *
 * ⚠️ The `WHERE user_id =` clause on `public_user_items` is the ONLY thing
 * hiding other sellers' listed inventory from anon today. Steps 1 and 3 must
 * ship in the SAME PR. `scripts/check-foundation.sh` warns on RETAIL_MODE=false
 * as a reminder. See docs/rls-anon-audit-2026-07-08.md § Followups.
 */
import { useAuth } from '@/hooks/useAuth';
import { PLATFORM_CONFIG } from '@/lib/platformConfig';

export const RETAIL_MODE = true;

/** Owner user_id — single seller in retail mode. */
export const OWNER_USER_ID = PLATFORM_CONFIG.ownerUserId;
export const OWNER_USERNAME = PLATFORM_CONFIG.ownerUsername;
/** Human-friendly display name shown in "Message {name}" copy. */
export const OWNER_DISPLAY_NAME = PLATFORM_CONFIG.ownerDisplayName;

/**
 * Hide every buyer-facing CTA (Message Seller, Make Offer, Propose Trade,
 * Propose Pickup, Sign In) for everyone except the owner when the site is in
 * retail mode. Owner / admin sessions keep the full chrome.
 */
export function useHideRetailBuyerCtas(): boolean {
	// In retail mode there is no buyer-to-seller chat surface, so the owner
	// should also see the storefront the way visitors do (no Contact Seller,
	// Ships/Trades/Buying badges, etc.). Owner-only management chrome is
	// gated separately via `showOwnerControls`.
	useAuth();
	return RETAIL_MODE;
}
